import { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthProvider";
import {
  User,
  Briefcase,
  Home,
  Euro,
  GraduationCap,
  ClipboardCheck,
  X,
  Mail,
  Wallet,
  Building2,
  FileText,
  BarChart3,
  CheckCircle2,
  ShieldAlert,
  Target
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "../../../components/ui/Input";
import CustomSelect from "../../../components/ui/CustomSelect";
import { Button } from "../../../components/ui/button";
import api from "../../../api"; // Assumes an axios instance is configured here
import { useNotification } from "../../../context/NotificationContext";

export default function FamilleAudit({ famille, open, onClose, onSave }) {
  const { showToast } = useNotification();
  const [step, setStep] = useState(1);
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    nomFamille: "",
    reference: "",
    statut: "Lead",
    regimeMatrimonial: "",
    contratMariage: "",
    dateMariage: "",
    nombreAdultes: 0,
    nombreEnfants: 0,
    familleRecomposee: false,
    residenceFiscale: "",
    paysResidence: "France",
    tmi: 0,
    ifiAssujetti: false,
    fiscaliteCommentaires: "",
    
    horizonInvestissement: "",
    besoinLiquidite: 0,
    sensibiliteRisque: "",
    contraintesJuridiques: "",
    contraintesFiscales: "",
    
    revenusAnnuels: 0,
    chargesAnnuelles: 0,
    epargneAnnuelle: 0,
    capaciteInvestissement: 0,
    
    patrimoineNet: 0,
    patrimoineImmobilier: 0,
    patrimoineFinancier: 0,
    patrimoineProfessionnel: 0,
    patrimoineAutres: 0,
    endettement: 0,
    
    profilInvestisseur: "",
    connaissanceFinanciere: "",
    toleranceRisque: "",
    
    transmissionPreparee: false,
    donationEffectuee: false,
    testament: false,
    protectionConjoint: false,
    
    pointsForts: "",
    pointsFaibles: "",
    opportunites: "",
    risques: "",
    recommandations: "",
    commentairesConseiller: "",
    
    scoreComplexite: 0,
    scoreUrgence: 0,
    scoreCommercial: 0,
    
    dateAudit: new Date().toISOString().split("T")[0],
    auditeur: user?.Nom || "Non assigné",
    signature: false,
  });

  useEffect(() => {
    if (famille && open) {
      setFormData(prev => ({
        ...prev,
        ...famille,
        nomFamille: famille.nomFamille || famille.nom || "",
        dateAudit: new Date().toISOString().split("T")[0],
        auditeur: user?.Nom || "Non assigné",
      }));
      setStep(1);
    }
  }, [famille, user, open]);

  const steps = [
    { id: 1, label: "Foyer", icon: User },
    { id: 2, label: "Fiscalité", icon: FileText },
    { id: 3, label: "Finances", icon: BarChart3 },
    { id: 4, label: "Patrimoine", icon: Home },
    { id: 5, label: "Profil & Risque", icon: ShieldAlert },
    { id: 6, label: "Transmission", icon: Target },
    { id: 7, label: "Analyse", icon: ClipboardCheck },
    { id: 8, label: "Validation", icon: CheckCircle2 },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : (type === "number" ? Number(value) : value)
    }));
  };

  const handleSubmit = async () => {
    try {
      if (famille?.id) {
        await api.put(`/familles/${famille.id}`, formData);
      } else {
        await api.post(`/familles`, formData);
      }
      showToast("Audit patrimonial enregistré avec succès !", "success");
      if (onSave) onSave();
      onClose();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement :", error);
      showToast("Erreur lors de l'enregistrement de l'audit.", "error");
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }} 
          className="absolute inset-0 bg-ink-900/60 backdrop-blur-sm"
          onClick={onClose}
        />
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="relative bg-surface w-full max-w-5xl max-h-[90vh] rounded-2xl shadow-hp-xl border border-workspace flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-workspace bg-surface-muted/30">
            <div>
              <h2 className="text-xl font-bold text-primary flex items-center gap-2">
                <ClipboardCheck className="w-6 h-6 text-info-500" />
                Audit Patrimonial : {formData.nomFamille || "Nouvelle Famille"}
              </h2>
              <p className="text-sm text-subtle mt-1">Saisie complète et structurée du dossier patrimonial</p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar Steps */}
            <div className="w-64 border-r border-workspace bg-surface-muted/10 p-4 overflow-y-auto hidden md:block">
              <nav className="space-y-1">
                {steps.map((s) => {
                  const Icon = s.icon;
                  const isActive = step === s.id;
                  return (
                    <button
                      key={s.id}
                      onClick={() => setStep(s.id)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                        isActive 
                          ? "bg-[var(--hp-color-primary)] text-[var(--hp-color-on-primary)] shadow-sm font-semibold" 
                          : "text-subtle hover:bg-workspace hover:text-primary"
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${isActive ? "text-[var(--hp-color-on-primary)]" : "text-subtle"}`} />
                      {s.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6 max-w-3xl"
                >
                  
                  {step === 1 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2"><User className="w-5 h-5"/> Structure du Foyer</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Nom de la Famille</label>
                          <Input type="text" name="nomFamille" value={formData.nomFamille} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Référence Dossier</label>
                          <Input type="text" name="reference" value={formData.reference} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Régime Matrimonial</label>
                          <Input type="text" name="regimeMatrimonial" value={formData.regimeMatrimonial} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Date de Mariage</label>
                          <Input type="date" name="dateMariage" value={formData.dateMariage?.split('T')[0] || ''} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Nombre d'adultes</label>
                          <Input type="number" name="nombreAdultes" value={formData.nombreAdultes} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Nombre d'enfants</label>
                          <Input type="number" name="nombreEnfants" value={formData.nombreEnfants} onChange={handleChange} className=" w-full" />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-4">
                        <input type="checkbox" name="familleRecomposee" checked={formData.familleRecomposee} onChange={handleChange} className="h-4 w-4 rounded border-workspace text-primary focus:ring-primary" />
                        <label className="text-sm font-medium text-primary">Famille Recomposée</label>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2"><FileText className="w-5 h-5"/> Fiscalité</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Résidence Fiscale</label>
                          <Input type="text" name="residenceFiscale" value={formData.residenceFiscale} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Pays de Résidence</label>
                          <Input type="text" name="paysResidence" value={formData.paysResidence} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">TMI (%)</label>
                          <Input type="number" name="tmi" value={formData.tmi} onChange={handleChange} className=" w-full" />
                        </div>
                        <div className="flex items-center gap-2 pt-6">
                          <input type="checkbox" name="ifiAssujetti" checked={formData.ifiAssujetti} onChange={handleChange} className="h-4 w-4 rounded border-workspace text-primary focus:ring-primary" />
                          <label className="text-sm font-medium text-primary">Assujetti à l'IFI</label>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-subtle mb-1">Commentaires Fiscaux</label>
                        <textarea name="fiscaliteCommentaires" value={formData.fiscaliteCommentaires} onChange={handleChange} className="workspace-input w-full h-24" />
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5"/> Capacité Financière</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Revenus Annuels Nets (€)</label>
                          <Input type="number" name="revenusAnnuels" value={formData.revenusAnnuels} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Charges Annuelles (€)</label>
                          <Input type="number" name="chargesAnnuelles" value={formData.chargesAnnuelles} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Épargne Annuelle (€)</label>
                          <Input type="number" name="epargneAnnuelle" value={formData.epargneAnnuelle} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Capacité d'Investissement (€)</label>
                          <Input type="number" name="capaciteInvestissement" value={formData.capaciteInvestissement} onChange={handleChange} className=" w-full" />
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 4 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2"><Home className="w-5 h-5"/> Synthèse Patrimoniale</h3>
                      <div className="p-4 bg-surface-muted rounded-xl border border-workspace mb-4">
                        <p className="text-xs text-subtle mb-1">Ces données peuvent être écrasées par la consolidation automatique.</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Patrimoine Financier (€)</label>
                          <Input type="number" name="patrimoineFinancier" value={formData.patrimoineFinancier} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Patrimoine Immobilier (€)</label>
                          <Input type="number" name="patrimoineImmobilier" value={formData.patrimoineImmobilier} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Patrimoine Professionnel (€)</label>
                          <Input type="number" name="patrimoineProfessionnel" value={formData.patrimoineProfessionnel} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Autres Actifs (€)</label>
                          <Input type="number" name="patrimoineAutres" value={formData.patrimoineAutres} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1 text-danger-500">Endettement Global (€)</label>
                          <Input type="number" name="endettement" value={formData.endettement} onChange={handleChange} className=" w-full border-danger-200 focus:border-danger-500" />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-primary mb-1">Patrimoine Net Estimé (€)</label>
                          <Input type="number" name="patrimoineNet" value={formData.patrimoineNet} onChange={handleChange} className=" w-full bg-surface-muted font-bold" readOnly />
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 5 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2"><ShieldAlert className="w-5 h-5"/> Profil Investisseur</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Profil Investisseur</label>
                          <CustomSelect
                            value={formData.profilInvestisseur}
                            onChange={(val) => setFormData({ ...formData, profilInvestisseur: val })}
                            options={[
                              { value: "Prudent", label: "Prudent" },
                              { value: "Equilibre", label: "Equilibré" },
                              { value: "Dynamique", label: "Dynamique" },
                              { value: "Agressif", label: "Agressif" }
                            ]}
                            placeholder="Sélectionner"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Tolérance au Risque</label>
                          <CustomSelect
                            value={formData.toleranceRisque}
                            onChange={(val) => setFormData({ ...formData, toleranceRisque: val })}
                            options={[
                              { value: "Faible", label: "Faible" },
                              { value: "Moyenne", label: "Moyenne" },
                              { value: "Forte", label: "Forte" }
                            ]}
                            placeholder="Sélectionner"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Horizon d'Investissement</label>
                          <Input type="text" name="horizonInvestissement" value={formData.horizonInvestissement} onChange={handleChange} className=" w-full" placeholder="Ex: 8 à 10 ans" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Besoin de Liquidité (€)</label>
                          <Input type="number" name="besoinLiquidite" value={formData.besoinLiquidite} onChange={handleChange} className=" w-full" />
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 6 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2"><Target className="w-5 h-5"/> Transmission</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-3 p-4 border border-workspace rounded-xl bg-surface-muted/30">
                          <input type="checkbox" name="transmissionPreparee" checked={formData.transmissionPreparee} onChange={handleChange} className="h-5 w-5 rounded border-workspace text-primary focus:ring-primary" />
                          <label className="text-sm font-medium text-primary">Transmission Préparée</label>
                        </div>
                        <div className="flex items-center gap-3 p-4 border border-workspace rounded-xl bg-surface-muted/30">
                          <input type="checkbox" name="donationEffectuee" checked={formData.donationEffectuee} onChange={handleChange} className="h-5 w-5 rounded border-workspace text-primary focus:ring-primary" />
                          <label className="text-sm font-medium text-primary">Donation Effectuée</label>
                        </div>
                        <div className="flex items-center gap-3 p-4 border border-workspace rounded-xl bg-surface-muted/30">
                          <input type="checkbox" name="testament" checked={formData.testament} onChange={handleChange} className="h-5 w-5 rounded border-workspace text-primary focus:ring-primary" />
                          <label className="text-sm font-medium text-primary">Testament Rédigé</label>
                        </div>
                        <div className="flex items-center gap-3 p-4 border border-workspace rounded-xl bg-surface-muted/30">
                          <input type="checkbox" name="protectionConjoint" checked={formData.protectionConjoint} onChange={handleChange} className="h-5 w-5 rounded border-workspace text-primary focus:ring-primary" />
                          <label className="text-sm font-medium text-primary">Protection du Conjoint</label>
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 7 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2"><ClipboardCheck className="w-5 h-5"/> Analyse & Scores</h3>
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Score Complexité /10</label>
                          <Input type="number" name="scoreComplexite" value={formData.scoreComplexite} onChange={handleChange} max="10" className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Score Urgence /10</label>
                          <Input type="number" name="scoreUrgence" value={formData.scoreUrgence} onChange={handleChange} max="10" className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Score Commercial /10</label>
                          <Input type="number" name="scoreCommercial" value={formData.scoreCommercial} onChange={handleChange} max="10" className=" w-full" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-subtle mb-1">Points Forts</label>
                        <textarea name="pointsForts" value={formData.pointsForts} onChange={handleChange} className="workspace-input w-full h-20" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-subtle mb-1">Points Faibles</label>
                        <textarea name="pointsFaibles" value={formData.pointsFaibles} onChange={handleChange} className="workspace-input w-full h-20" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-subtle mb-1">Recommandations Stratégiques</label>
                        <textarea name="recommandations" value={formData.recommandations} onChange={handleChange} className="workspace-input w-full h-24 bg-surface text-primary border-workspace" />
                      </div>
                    </div>
                  )}

                  {step === 8 && (
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-success-500"/> Validation de l'Audit</h3>
                      
                      <div className="bg-success-50 dark:bg-success-900/20 text-success-800 dark:text-success-300 p-4 rounded-xl border border-success-100 dark:border-success-800/50">
                        <p className="font-medium text-sm mb-2">En validant cet audit :</p>
                        <ul className="list-disc list-inside text-sm space-y-1">
                          <li>Les données de la famille seront mises à jour.</li>
                          <li>Une archive horodatée (AuditPatrimonial) sera créée pour l'historique.</li>
                        </ul>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Date de l'Audit</label>
                          <Input type="date" name="dateAudit" value={formData.dateAudit} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Auditeur Responsable</label>
                          <Input type="text" name="auditeur" value={formData.auditeur} onChange={handleChange} className=" w-full" disabled />
                        </div>
                      </div>

                      <div className="pt-4 flex justify-end gap-3">
                        <Button variant="outline" onClick={onClose}>Annuler</Button>
                        <Button variant="primary" onClick={handleSubmit} className="gap-2">
                          <CheckCircle2 className="w-5 h-5" />
                          Sauvegarder et Archiver
                        </Button>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Footer Nav */}
          <div className="px-6 py-4 border-t border-workspace bg-surface-muted/30 flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() => setStep(Math.max(1, step - 1))}
              disabled={step === 1}
            >
              Précédent
            </Button>
            <div className="flex gap-1.5">
              {steps.map(s => (
                <div key={s.id} className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${step === s.id ? "bg-[var(--hp-color-primary)] scale-110" : "bg-[var(--hp-color-border-strong)] opacity-50"}`} />
              ))}
            </div>
            {step < steps.length ? (
              <Button variant="primary" onClick={() => setStep(step + 1)}>
                Suivant
              
              </Button>
            ) : (
              <Button variant="primary" onClick={handleSubmit} className="bg-success-600 hover:bg-success-700 text-white border-success-600">
                Terminer
              
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
